import { Component, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SystemsService } from '../api/services/systems.service';
import { SystemModel, SystemModelPagedResponseModel, QueryParams } from '../api/models';
import { AuthService } from '../shared/auth-service/auth.service';
import { AppToastService } from '../shared/toast-service/toast-service';
import { DatePipe } from '@angular/common';

// Make Math available in template
declare var Math: any;

@Component({
    selector: 'app-systems',
    templateUrl: './systems.component.html',
    styleUrls: ['./systems.component.scss'],
    providers: [DatePipe],
    standalone: false,
})
export class SystemsComponent implements OnInit {
    public loggedIn!: boolean;
    title = 'Systems Management';
    systems: SystemModel[] = [];
    filteredSystems: SystemModel[] = [];
    systemArticleCounts: { [systemId: string]: number } = {};
    loading = false;
    searchTerm = '';
    selectedSystem: SystemModel | null = null;
    modalContent: SystemModel | undefined;
    displayError = false;
    errorMessage = '';

    // Pagination
    currentPage = 1;
    itemsPerPage = 50;
    totalItems = 0;

    // Filter options
    filterOptions = [
        { label: 'Aluminum', selected: false },
        { label: 'Steel', selected: false },
        { label: 'Composite', selected: false }
    ];

    // Sorting
    sortColumn: string = '';
    sortDirection: 'asc' | 'desc' = 'asc';

    constructor(
        private systemsService: SystemsService,
        private modalService: NgbModal,
        private authService: AuthService,
        private toastService: AppToastService,
        private datePipe: DatePipe
    ) { }

    ngOnInit(): void {
        this.loggedIn = this.isLoggedIn();
        this.loadSystems();
    }

    private isLoggedIn(): boolean {
        return !!this.authService.getToken();
    }

    public logout(): void {
        this.authService.logout();
    }

    loadSystems(): void {
        this.loading = true;
        const params: QueryParams = {
            page: this.currentPage,
            pageSize: this.itemsPerPage
        };

        this.systemsService.getSystems(params).subscribe({
            next: (response: SystemModelPagedResponseModel | any) => {
                // Handle both camelCase (new) and PascalCase (old) formats
                const data = response.data || response.Data || [];
                // Transform systems from PascalCase to camelCase if needed
                this.systems = Array.isArray(data) 
                    ? data.map(system => this.transformSystemIfNeeded(system))
                    : [];
                this.filteredSystems = [...this.systems];
                // Use totalCount first, then fallback to pagination.totalItems
                // Handle both camelCase and PascalCase
                this.totalItems = response.totalCount || response.TotalCount || 
                                 response.pagination?.totalItems || 
                                 response.Pagination?.TotalItems || 0;
                this.loading = false;

                // Load article counts for each system
                this.loadArticleCounts();
            },
            error: (error) => {
                console.error('Error loading systems:', error);
                this.showError('Could not load systems.');
                this.loading = false;
            }
        });
    }

    loadArticleCounts(): void {
        this.systems.forEach(system => {
            if (system.id) {
                // Use the articles array length or articleIds array length
                const count = system.articles?.length || system.articleIds?.length || 0;
                this.systemArticleCounts[system.id!] = count;
            }
        });
    }

    // Transform system object from PascalCase to camelCase if needed
    private transformSystemIfNeeded(system: any): SystemModel {
        if (!system) return system;
        if (typeof system !== 'object') return system;
        
        // Check if already in camelCase (has lowercase properties)
        if (system.name !== undefined || system.id !== undefined) {
            // Already in camelCase, but check nested objects and return a new object to avoid mutation
            const transformed: SystemModel = {
                ...system,
                regions: system.regions && Array.isArray(system.regions)
                    ? system.regions.map((region: any) => this.transformRegionIfNeeded(region))
                    : system.regions
            };
            return transformed;
        }
        
        // Transform from PascalCase to camelCase
        const transformed: SystemModel = {
            id: system.Id || system.id,
            name: system.Name || system.name || '',
            version: system.Version || system.version,
            description: system.Description || system.description,
            material: system.Material || system.material,
            type: system.Type || system.type,
            regions: (system.Regions || system.regions || []).map((region: any) => this.transformRegionIfNeeded(region)),
            articleIds: system.ArticleIds || system.articleIds,
            articles: system.Articles || system.articles,
            createdAt: system.CreatedAt || system.createdAt,
            updatedAt: system.UpdatedAt || system.updatedAt
        };
        
        return transformed;
    }

    // Transform region object from PascalCase to camelCase if needed
    private transformRegionIfNeeded(region: any): any {
        if (!region) return region;
        if (typeof region !== 'object') return region;
        
        // Check if already in camelCase
        if (region.country !== undefined || region.id !== undefined) {
            return region;
        }
        
        // Transform from PascalCase to camelCase
        return {
            id: region.Id || region.id,
            country: region.Country || region.country,
            city: region.City || region.city,
            state: region.State || region.state,
            district: region.District || region.district,
            place: region.Place || region.place
        };
    }

    getArticleCount(systemId: string): number {
        return this.systemArticleCounts[systemId] || 0;
    }

    search(event: Event): void {
        event.preventDefault();
        if (this.searchTerm.trim()) {
            this.filteredSystems = this.systems.filter(system => {
                const name = system.name || '';
                const material = system.material || '';
                const type = system.type || '';
                return name.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
                       material.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
                       type.toLowerCase().includes(this.searchTerm.toLowerCase());
            });
        } else {
            this.filteredSystems = [...this.systems];
        }
    }

    onFilterChange(filter: any, event: Event): void {
        filter.selected = (event.target as HTMLInputElement).checked;
        this.applyFilters();
    }

    removeFilter(filter: any): void {
        filter.selected = false;
        this.applyFilters();
    }

    applyFilters(): void {
        const selectedFilters = this.filterOptions.filter(f => f.selected).map(f => f.label);

        if (selectedFilters.length === 0) {
            this.filteredSystems = [...this.systems];
        } else {
            this.filteredSystems = this.systems.filter(system => {
                const material = system.material || '';
                return selectedFilters.some(filter =>
                    material.toLowerCase().includes(filter.toLowerCase())
                );
            });
        }
        this.applySorting();
    }

    onSort(column: string): void {
        if (this.sortColumn === column) {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortColumn = column;
            this.sortDirection = 'asc';
        }
        this.applySorting();
    }

    applySorting(): void {
        if (!this.sortColumn) {
            return;
        }

        this.filteredSystems.sort((a, b) => {
            let aValue: any;
            let bValue: any;

            switch (this.sortColumn) {
                case 'name':
                    aValue = (a.name || '').toLowerCase();
                    bValue = (b.name || '').toLowerCase();
                    break;
                case 'material':
                    aValue = (a.material || '').toLowerCase();
                    bValue = (b.material || '').toLowerCase();
                    break;
                case 'type':
                    aValue = (a.type || '').toLowerCase();
                    bValue = (b.type || '').toLowerCase();
                    break;
                case 'createdAt':
                    aValue = new Date(a.createdAt || 0).getTime();
                    bValue = new Date(b.createdAt || 0).getTime();
                    break;
                case 'updatedAt':
                    aValue = new Date(a.updatedAt || 0).getTime();
                    bValue = new Date(b.updatedAt || 0).getTime();
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) {
                return this.sortDirection === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return this.sortDirection === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }

    getSortIcon(column: string): string {
        if (this.sortColumn !== column) {
            return 'bi-arrow-down-up';
        }
        return this.sortDirection === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down';
    }

    getSelectedFilters(): any[] {
        return this.filterOptions.filter(filter => filter.selected);
    }

    clearAllFilters(): void {
        this.searchTerm = '';
        this.filterOptions.forEach(filter => filter.selected = false);
        this.currentPage = 1;
        this.filteredSystems = [...this.systems];
    }

    openSystemDetailModal(modal: any, system: SystemModel): void {
        // Ensure system is transformed before displaying
        this.modalContent = this.transformSystemIfNeeded(system);
        this.modalService.open(modal, {
            ariaLabelledBy: 'modal-basic-title',
            windowClass: 'fluid-modal',
        });
    }

    openCreateSystemModal(modal: any): void {
        this.modalContent = {
            name: '',
            material: '',
            type: ''
        } as SystemModel;
        this.modalService.open(modal, {
            ariaLabelledBy: 'modal-basic-title',
            windowClass: 'fluid-modal',
        });
    }

    openEditSystemModal(modal: any, system: SystemModel): void {
        // Ensure system is transformed before editing
        this.modalContent = { ...this.transformSystemIfNeeded(system) };
        this.modalService.open(modal, {
            ariaLabelledBy: 'modal-basic-title',
            windowClass: 'fluid-modal',
        });
    }

    saveSystem(system: SystemModel): void {
        this.loading = true;
        if (system.id) {
            // Update existing system
            this.systemsService.updateSystem(system.id, system).subscribe({
                next: (response) => {
                    this.toastService.show('System updated successfully', {
                        classname: 'bg-success text-light',
                        delay: 4000
                    });
                    this.loadSystems();
                    this.modalService.dismissAll();
                },
                error: (error) => {
                    console.error('Error updating system:', error);
                    this.showError('Could not update system.');
                    this.loading = false;
                }
            });
        } else {
            // Create new system
            this.systemsService.createSystem(system).subscribe({
                next: (response) => {
                    this.toastService.show('System created successfully', {
                        classname: 'bg-success text-light',
                        delay: 4000
                    });
                    this.loadSystems();
                    this.modalService.dismissAll();
                },
                error: (error) => {
                    console.error('Error creating system:', error);
                    this.showError('Could not create system.');
                    this.loading = false;
                }
            });
        }
    }

    deleteSystem(system: SystemModel): void {
        // Ensure system is transformed to get correct id and name
        const transformedSystem = this.transformSystemIfNeeded(system);
        if (transformedSystem.id && confirm(`Are you sure you want to delete system "${transformedSystem.name}"?`)) {
            this.loading = true;
            this.systemsService.deleteSystem(transformedSystem.id).subscribe({
                next: () => {
                    this.toastService.show('System deleted successfully', {
                        classname: 'bg-success text-light',
                        delay: 4000
                    });
                    this.loadSystems();
                },
                error: (error) => {
                    console.error('Error deleting system:', error);
                    this.showError('Could not delete system.');
                    this.loading = false;
                }
            });
        }
    }

    onPageChange(page: number, event?: Event): void {
        if (event) {
            event.preventDefault();
        }

        // Validate page number
        if (page < 1 || page > this.getTotalPages()) {
            console.warn('Invalid page number:', page);
            return;
        }

        this.currentPage = page;
        this.loadSystems();
    }

    showError(message: string): void {
        this.displayError = true;
        this.errorMessage = message;
    }

    formatDate(date: string | Date | undefined): string {
        return date ? this.datePipe.transform(date, 'MMM-dd-yyyy hh:mm a') || '' : '';
    }

    getTotalPages(): number {
        return Math.ceil(this.totalItems / this.itemsPerPage);
    }

    getPagesArray(): number[] {
        const totalPages = this.getTotalPages();
        const maxVisiblePages = 7; // Show max 7 page numbers

        if (totalPages <= maxVisiblePages) {
            return Array(totalPages).fill(0).map((_, i) => i + 1);
        }

        const startPage = Math.max(1, this.currentPage - Math.floor(maxVisiblePages / 2));
        const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        return Array(endPage - startPage + 1).fill(0).map((_, i) => startPage + i);
    }

    getItemRangeStart(): number {
        return (this.currentPage - 1) * this.itemsPerPage + 1;
    }

    getItemRangeEnd(): number {
        return Math.min(this.currentPage * this.itemsPerPage, this.totalItems);
    }
}
